/**
 * vqeDdlStore — transient UI state for the Visual DDL Editor.
 *
 * Owns three slices:
 *   - `pendingEdit` — Slice H1 threshold-gated preview (PreviewDialog).
 *   - `pendingBlastRadius` — Slice H3 pre-destructive guard (BlastRadiusPanel).
 *   - `miniGraphOpenByObject` — Slice H3 per-object opt-in mini-graph
 *     side-panel toggle (Designer surface).
 *
 * Buffer is the source of truth for DDL — this store holds only ephemeral
 * UI state and never persists.
 */

import { create } from 'zustand';
import type { DdlEdit } from '../../../sql-resolver/types';
import type { DependencyItem } from '../dependencies/types';
import type { VqeDdlPendingEdit } from '../edit/commitDdlEdit';

export type VqeDdlPendingBlastRadius = {
  readonly edit: DdlEdit;
  readonly documentId: string;
  readonly statementId: string;
  /** Identifies the editor surface that staged the gate — used by the panel
   *  for analytics + by host UI to scope the "Jump to" hand-off. */
  readonly source: 'inline' | 'designer';
  /** Object whose dependents are at risk — surfaced in the panel header. */
  readonly target: {
    readonly schema: string | null;
    readonly objectName: string | null;
    /** Pretty-printed action ("Drop column", "Drop view", …). */
    readonly actionLabel: string;
    /** Pretty-printed target ("rating", "public.user_orders", …). May be
     *  null when the action is unambiguous on its own (e.g. drop-primary-key). */
    readonly targetLabel: string | null;
  };
  /** Dependents the user must acknowledge before commit. Render in panel rows. */
  readonly dependents: readonly DependencyItem[];
  /** VDE-9 fail-safe: true when dependency annotations were NOT a confirmed
   *  query success, so `dependents` is empty only because the answer is
   *  UNKNOWN — not because there are none. The panel must say dependents
   *  could not be verified instead of "No dependents recorded.". Optional so
   *  existing call sites (verified path) stay unaffected. */
  readonly dependentsUnverified?: boolean;
  /** Resume thunk — flushes the original commit through the rest of the
   *  pipeline (preview-or-direct apply). The panel calls this on Confirm. */
  readonly proceed: () => Promise<void>;
};

export type VqeDdlMiniGraphState = {
  readonly objectKey: string;
  readonly open: boolean;
};

export type VqeDdlState = {
  pendingEdit: VqeDdlPendingEdit | null;
  setPendingEdit: (pending: VqeDdlPendingEdit | null) => void;
  commitPending: () => void;
  cancelPending: () => void;

  pendingBlastRadius: VqeDdlPendingBlastRadius | null;
  setPendingBlastRadius: (pending: VqeDdlPendingBlastRadius | null) => void;
  commitBlastRadius: () => void;
  cancelBlastRadius: () => void;

  /** Per-object mini-graph open/closed flag. Keyed on a stable
   *  `connectionId|database|schema|objectName` string so multiple Designer
   *  tabs each track their own toggle without leaking across tabs. */
  miniGraphOpenByObject: Record<string, boolean>;
  setMiniGraphOpen: (objectKey: string, open: boolean) => void;
};

export const initialVqeDdlState: Pick<
  VqeDdlState,
  'pendingEdit' | 'pendingBlastRadius' | 'miniGraphOpenByObject'
> = {
  pendingEdit: null,
  pendingBlastRadius: null,
  miniGraphOpenByObject: {},
};

export const useVqeDdlStore = create<VqeDdlState>((set) => ({
  ...initialVqeDdlState,
  setPendingEdit: (pendingEdit) => set({ pendingEdit }),
  commitPending: () => set({ pendingEdit: null }),
  cancelPending: () => set({ pendingEdit: null }),

  setPendingBlastRadius: (pendingBlastRadius) => set({ pendingBlastRadius }),
  commitBlastRadius: () => set({ pendingBlastRadius: null }),
  cancelBlastRadius: () => set({ pendingBlastRadius: null }),

  setMiniGraphOpen: (objectKey, open) =>
    set((state) => ({
      miniGraphOpenByObject: {
        ...state.miniGraphOpenByObject,
        [objectKey]: open,
      },
    })),
}));

export function resetVqeDdlStoreForTests(): void {
  useVqeDdlStore.setState({ ...initialVqeDdlState });
}

/** Stable key for the per-object mini-graph map. Keep in lockstep with
 *  `useDependencyAnnotations`'s queryKey shape so toggle state matches the
 *  same scoping. */
export function miniGraphObjectKey(args: {
  readonly connectionId: string | null | undefined;
  readonly database: string | null | undefined;
  readonly schema: string | null | undefined;
  readonly objectName: string | null | undefined;
}): string {
  return [
    args.connectionId ?? '',
    args.database ?? '',
    args.schema ?? '',
    args.objectName ?? '',
  ].join('|');
}
